import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Plus, X, Search, Users, Briefcase, Shield, Copy,
  Edit2, Trash2, Check, AlertCircle, UserCheck, UserX,
  Building2, Phone, Mail, Calendar, ChevronDown, ChevronRight,
  MoreVertical, Filter, RefreshCw, Eye, Sparkles, KeyRound, AlertTriangle,
  Send, Link,
} from 'lucide-react';

import '../style/EmployeeManagement.css';
import { host } from '../script/variables';

/* ═══════════════════════════════════════════════════════════════════════════
   API layer
   ═════════════════════════════════════════════════════════════════════════ */

const BASE = '/employeeapi'; // matches app.use('/partner', employeeManagementRoutes)

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: localStorage.getItem('partnertoken') || '',
});

/**
 * Every backend route answers { ok, data, message?, ... }. Anything else — an
 * HTML error page from a proxy, a network drop — is turned into a thrown
 * ApiError so callers only ever deal with one failure shape.
 */
class ApiError extends Error {
  constructor(message, payload = {}, status = 0) {
    super(message);
    this.payload = payload;
    this.status = status;
  }
}

async function api(path, { method = 'GET', body, signal } = {}) {
  let res;
  try {
    res = await fetch(`${host}${BASE}${path}`, {
      method,
      headers: authHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Could not reach the server. Check your connection.');
  }

  let payload = {};
  try { payload = await res.json(); } catch { /* non-JSON body */ }

  if (!res.ok || payload.ok === false) {
    throw new ApiError(payload.message || `Request failed (${res.status})`, payload, res.status);
  }
  return payload;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Constants & tiny helpers
   ═════════════════════════════════════════════════════════════════════════ */

/* Brand hex rather than var() — these get interpolated into rgba-ish
   strings (`${color}1f`) for tinted backgrounds, which var() can't do.
   Values mirror --success / --warning / --error in theme.css. */
const STATUS_CONFIG = {
  active:     { label: 'Active',     color: '#2E7D52' },
  inactive:   { label: 'Inactive',   color: '#C79A3B' },
  terminated: { label: 'Terminated', color: '#B5432F' },
};

const PALETTE = ['#24455C', '#c0392b', '#2980b9', '#8e44ad', '#16a085', '#d35400', '#2c3e50', '#27ae60'];

const RISK_LABEL = { high: 'Sensitive', critical: 'Critical' };

/** Deterministic avatar colour that never throws on empty names. */
const avatarColor = (name = '') => PALETTE[(name.charCodeAt(0) || 0) % PALETTE.length];
const initials = (first = '', last = '') =>
  `${(first[0] || '?').toUpperCase()}${(last[0] || '').toUpperCase()}`;

/* ═══════════════════════════════════════════════════════════════════════════
   Hooks
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Below this width the employee table becomes a card list — a six-column
 * table sideways-scrolling on a phone is the worst version of this screen.
 * Kept in sync with the same breakpoint in EmployeeManagement.css.
 */
const COMPACT_QUERY = '(max-width: 900px)';

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = e => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

/** Escape closes, and the page behind a dialog stops scrolling under it. */
const useDialogBehaviour = (onClose) => {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') close.current(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, []);
};

/** Closes on an outside click or Escape — shared by every popover here. */
const useDismissable = (open, onDismiss) => {
  const ref = useRef(null);
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) dismiss.current(); };
    const onKey = e => { if (e.key === 'Escape') dismiss.current(); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return ref;
};

/* ═══════════════════════════════════════════════════════════════════════════
   ModalShell — one overlay implementation for every dialog on this screen.
   Full-height sheet on phones, centred card on desktop (see the CSS).
   ═════════════════════════════════════════════════════════════════════════ */

const ModalShell = ({ size = '', role = 'dialog', labelledBy, onClose, children }) => {
  useDialogBehaviour(onClose);
  return (
    <div className="em-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`em-modal ${size}`} role={role} aria-modal="true" aria-labelledby={labelledBy}>
        {children}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Toast
   ═════════════════════════════════════════════════════════════════════════ */

const Toast = ({ msg, type, onClose }) => (
  <div className={`em-toast em-toast--${type}`} role="status" aria-live="polite">
    {type === 'error' ? <AlertCircle size={15} /> : <Check size={15} />}
    <span>{msg}</span>
    <button onClick={onClose} aria-label="Dismiss"><X size={13} /></button>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Confirm dialog — replaces window.confirm so we can offer real choices
   ═════════════════════════════════════════════════════════════════════════ */

const ConfirmDialog = ({ title, body, choices, onClose }) => (
  <ModalShell size="em-modal--sm" role="alertdialog" labelledBy="em-confirm-title" onClose={onClose}>
    <div className="em-modal-head">
      <h3 id="em-confirm-title">{title}</h3>
      <button className="em-close-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
    </div>
    <div className="em-modal-body">{body}</div>
    <div className="em-modal-foot">
      <button className="em-btn-ghost" onClick={onClose}>Cancel</button>
      {choices.map(c => (
        <button
          key={c.label}
          className={c.danger ? 'em-btn-danger' : 'em-btn-primary'}
          onClick={() => { onClose(); c.onSelect(); }}
        >
          {c.label}
        </button>
      ))}
    </div>
  </ModalShell>
);

/* ═══════════════════════════════════════════════════════════════════════════
   ChipRow — caps a list of chips so one employee with nine outlets can't
   blow the row height out. The overflow count stays clickable-free on
   purpose; the full list lives in the profile drawer.
   ═════════════════════════════════════════════════════════════════════════ */

const ChipRow = ({ items, max = 3, empty = '—', render }) => {
  if (!items || items.length === 0) return <span className="em-muted">{empty}</span>;
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;
  return (
    <div className="em-chip-row-inline">
      {shown.map(render)}
      {rest > 0 && <span className="em-chip em-chip--more">+{rest}</span>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PermissionPicker — the heart of the dynamic role system
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Renders the server's permission catalog as a grouped, searchable checklist.
 * Nothing here is hardcoded: ship a permission on the backend and it shows up
 * on the next page load.
 */
const PermissionPicker = ({ catalog, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [openModules, setOpenModules] = useState(() => new Set(catalog.map(m => m.key)));

  const selected = useMemo(() => new Set(value || []), [value]);

  /** A key counts as held if it's listed directly or covered by a wildcard. */
  const isHeld = key => {
    if (selected.has('*')) return true;
    if (selected.has(key)) return true;
    return selected.has(`${key.split(':')[0]}:*`);
  };

  const toggle = key => {
    const next = new Set(selected);
    // Expand any wildcard that covers this key so the removal actually sticks.
    const mod = key.split(':')[0];
    if (next.has('*') || next.has(`${mod}:*`)) {
      next.delete('*');
      next.delete(`${mod}:*`);
      catalog.forEach(m => {
        if (m.key === mod || selected.has('*')) {
          m.permissions.forEach(p => next.add(p.key));
        }
      });
    }
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(Array.from(next).sort());
  };

  const toggleModule = mod => {
    const keys = mod.permissions.map(p => p.key);
    const allHeld = keys.every(isHeld);
    const next = new Set(selected);
    next.delete('*');
    next.delete(`${mod.key}:*`);
    if (selected.has('*')) catalog.forEach(m => m.permissions.forEach(p => next.add(p.key)));
    keys.forEach(k => (allHeld ? next.delete(k) : next.add(k)));
    onChange(Array.from(next).sort());
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    return catalog
      .map(m => ({
        ...m,
        permissions: m.permissions.filter(p =>
          p.label.toLowerCase().includes(q) ||
          p.key.toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q)
        ),
      }))
      .filter(m => m.permissions.length > 0 || m.label.toLowerCase().includes(q));
  }, [catalog, query]);

  const totalHeld = catalog.reduce(
    (n, m) => n + m.permissions.filter(p => isHeld(p.key)).length, 0
  );
  const totalAvailable = catalog.reduce((n, m) => n + m.permissions.length, 0);

  if (!catalog || catalog.length === 0) {
    return (
      <div className="em-perm-picker">
        <p className="em-muted em-form-note em-perm-picker-empty">
          The permission list didn’t load, so there’s nothing to choose from. Refresh the page to edit permissions. Saving now keeps the ones this role already has.
        </p>
      </div>
    );
  }

  return (
    <div className="em-perm-picker">
      <div className="em-perm-picker-bar">
        <div className="em-search-wrap em-search-wrap--inline">
          <Search size={14} className="em-search-icon" />
          <input
            className="em-search"
            placeholder="Search permissions…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <span className="em-perm-count">{totalHeld} of {totalAvailable} selected</span>
      </div>

      {filtered.length === 0 && <p className="em-muted em-form-note">No permissions match “{query}”.</p>}

      {filtered.map(mod => {
        const held = mod.permissions.filter(p => isHeld(p.key)).length;
        const open = openModules.has(mod.key) || Boolean(query);
        return (
          <div className="em-perm-module" key={mod.key}>
            <div className="em-perm-module-head">
              <button
                type="button"
                className="em-perm-module-toggle"
                onClick={() => setOpenModules(s => {
                  const n = new Set(s);
                  n.has(mod.key) ? n.delete(mod.key) : n.add(mod.key);
                  return n;
                })}
                aria-expanded={open}
              >
                {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <span className="em-perm-module-name">{mod.label}</span>
                <span className={`em-perm-module-count ${held ? 'has' : ''}`}>
                  {held}/{mod.permissions.length}
                </span>
              </button>
              <button
                type="button"
                className="em-link-btn"
                onClick={() => toggleModule(mod)}
              >
                {mod.permissions.every(p => isHeld(p.key)) ? 'Clear all' : 'Select all'}
              </button>
            </div>

            {open && (
              <div className="em-perm-module-body">
                {mod.permissions.map(p => {
                  const on = isHeld(p.key);
                  return (
                    <label className={`em-perm-row ${on ? 'on' : ''}`} key={p.key}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(p.key)}
                      />
                      <span className="em-perm-row-main">
                        <span className="em-perm-row-label">
                          {p.label}
                          {p.risk && (
                            <span className={`em-risk-tag em-risk-tag--${p.risk}`}>
                              <AlertTriangle size={10} /> {RISK_LABEL[p.risk]}
                            </span>
                          )}
                        </span>
                        {p.description && <span className="em-perm-row-desc">{p.description}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═════════════════════════════════════════════════════════════════════════ */

const EmployeeManagement = ({ properties = [] }) => {
  const [tab, setTab] = useState('employees');

  const [employees,    setEmployees]    = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles,        setRoles]        = useState([]);
  const [catalog,      setCatalog]      = useState([]);
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null);

  const [empModal,    setEmpModal]    = useState(false);
  const [desigModal,  setDesigModal]  = useState(false);
  const [roleModal,   setRoleModal]   = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [confirm,     setConfirm]     = useState(null);

  const [editEmp,   setEditEmp]   = useState(null);
  const [editDesig, setEditDesig] = useState(null);
  const [editRole,  setEditRole]  = useState(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((msg, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /* ── load ─────────────────────────────────────────────────────────────── */
  const loadAll = useCallback(async (signal) => {
    setLoading(true);
    setLoadError(null);
    try {
      const [cat, e, d, r, s] = await Promise.all([
        api('/permissions', { signal }),
        api('/employees?limit=200', { signal }),
        api('/designations?includeUsage=true', { signal }),
        api('/roles?includeUsage=true', { signal }),
        api('/employees/stats', { signal }),
      ]);
      setCatalog(cat.data?.modules || []);
      setEmployees(Array.isArray(e.data) ? e.data : []);
      setDesignations(Array.isArray(d.data) ? d.data : []);
      setRoles(Array.isArray(r.data) ? r.data : []);
      setStats(s.data || null);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setLoadError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    const ctrl = new AbortController();
    loadAll(ctrl.signal);
    return () => ctrl.abort();
  }, [loadAll]);

  /** Wrap a mutation: surface backend messages, refresh on success. */
  const run = useCallback(async (fn, successMsg, { silentError = false } = {}) => {
    try {
      const result = await fn();
      if (result?.warning) showToast(result.warning, 'error');
      else if (successMsg) showToast(result?.message || successMsg);
      await loadAll();
      return result;
    } catch (err) {
      // Callers that handle a specific failure with their own dialog pass
      // silentError, so the user doesn't get a red toast and a modal at once.
      if (!silentError) showToast(err.message, 'error');
      throw err;
    }
  }, [loadAll, showToast]);

  /* ── delete flows ─────────────────────────────────────────────────────── */

  const deleteRole = (role) => {
    setConfirm({
      title: `Delete ${role.name}?`,
      body: (
        <p className="em-confirm-text">
          Staff holding designations that use this role will lose the permissions it grants.
        </p>
      ),
      choices: [{
        label: 'Delete role', danger: true,
        onSelect: async () => {
          try {
            await run(() => api(`/roles/${role._id}`, { method: 'DELETE' }), 'Role deleted', { silentError: true });
          } catch (err) {
            if (err.payload?.code !== 'ROLE_IN_USE') { showToast(err.message, 'error'); return; }
            setConfirm({
              title: `${role.name} is still in use`,
              body: (
                <>
                  <p className="em-confirm-text">
                    It is attached to {err.payload.usedByDesignations} designation
                    {err.payload.usedByDesignations > 1 ? 's' : ''}:
                  </p>
                  <div className="em-chip-row">
                    {(err.payload.designationNames || []).map(n => (
                      <span key={n} className="em-chip">{n}</span>
                    ))}
                  </div>
                </>
              ),
              choices: [{
                label: 'Remove it anyway', danger: true,
                onSelect: () => run(
                  () => api(`/roles/${role._id}?force=true`, { method: 'DELETE' }),
                  'Role deleted'
                ).catch(() => {}),
              }],
            });
          }
        },
      }],
    });
  };

  const deleteDesignation = (d) => {
    const others = designations.filter(x => x._id !== d._id);
    setConfirm({
      title: `Delete ${d.name}?`,
      body: <p className="em-confirm-text">Employees holding this designation will lose its permissions.</p>,
      choices: [{
        label: 'Delete designation', danger: true,
        onSelect: async () => {
          try {
            await run(() => api(`/designations/${d._id}`, { method: 'DELETE' }), 'Designation deleted', { silentError: true });
          } catch (err) {
            if (err.payload?.code !== 'DESIGNATION_IN_USE') { showToast(err.message, 'error'); return; }
            setConfirm({
              title: `${d.name} is held by ${err.payload.employeeCount} employee${err.payload.employeeCount > 1 ? 's' : ''}`,
              body: (
                <>
                  <p className="em-confirm-text">Move them to another designation, or remove it and leave them with no title here.</p>
                  {others.length > 0 && (
                    <div className="em-chip-row">
                      {others.map(o => (
                        <button
                          key={o._id}
                          className="em-chip-pick"
                          style={{ borderColor: o.color, color: o.color }}
                          onClick={() => {
                            setConfirm(null);
                            run(
                              () => api(`/designations/${d._id}?reassignTo=${o._id}`, { method: 'DELETE' }),
                              `Moved to ${o.name}`
                            ).catch(() => {});
                          }}
                        >
                          Move to {o.name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ),
              choices: [{
                label: 'Remove anyway', danger: true,
                onSelect: () => run(
                  () => api(`/designations/${d._id}?force=true`, { method: 'DELETE' }),
                  'Designation deleted'
                ).catch(() => {}),
              }],
            });
          }
        },
      }],
    });
  };

  /* ── responsive shape ─────────────────────────────────────────────────── */
  const isCompact = useMediaQuery(COMPACT_QUERY);

  /**
   * Secondary employee actions. Only View and Edit stay on the surface —
   * five equal-weight icons in a row made every one of them read as noise.
   */
  const empActions = useCallback((emp) => {
    const cafeId = emp.assignments?.[0]?.cafeId?._id || emp.assignments?.[0]?.cafeId || '';
    const items = [
      // {
      //   key: 'link', icon: Link, label: 'Copy login link',
      //   onSelect: () => {
      //     const url = cafeId
      //       ? `${window.location.origin}/login?cafe=${cafeId}`
      //       : `${window.location.origin}/login`;
      //     navigator.clipboard.writeText(url).then(() => showToast('Login link copied!'));
      //   },
      // },
      {
        key: 'resend', icon: Send, label: 'Resend credentials',
        onSelect: () => run(
          () => api(`/employees/${emp._id}/resend-credentials`, { method: 'POST', body: { cafeId } }),
          'Login link sent via SMS & email'
        ).catch(() => {}),
      },
    ];
    ['active', 'inactive', 'terminated']
      .filter(s => s !== emp.status)
      .forEach((s, i) => items.push({
        key: `status-${s}`,
        icon: s === 'active' ? UserCheck : UserX,
        label: `Mark ${STATUS_CONFIG[s].label.toLowerCase()}`,
        color: STATUS_CONFIG[s].color,
        divided: i === 0,
        onSelect: () => run(
          () => api(`/employees/${emp._id}/status`, { method: 'PATCH', body: { status: s } }),
          'Status updated'
        ).catch(() => {}),
      }));
    return items;
  }, [run, showToast]);

  /* ── filtered employees ───────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter(e => {
      const haystack = [
        e.firstName, e.lastName, e.email, e.phone, e.employeeCode,
      ].filter(Boolean).join(' ').toLowerCase();
      const matchSearch = !q || haystack.includes(q);
      const matchStatus = !filterStatus || e.status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [employees, search, filterStatus]);

  /* ── render ───────────────────────────────────────────────────────────── */
  return (
    <div className="em-root">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {confirm && <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />}

      <header className="em-header">
        <div className="em-header-text">
          <h2 className="em-header-title">Employee Management</h2>
          <p className="em-header-sub">Build your own roles, group them into designations, and assign staff across outlets</p>
        </div>
        <div className="em-header-actions">
          <button
            className="em-btn-ghost em-btn-ghost--icon"
            onClick={() => loadAll()}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'em-spin' : ''} />
          </button>
          <button className="em-btn-primary" onClick={() => { setEditEmp(null); setEmpModal(true); }}>
            <Plus size={16} /> <span>Add employee</span>
          </button>
        </div>
      </header>

      {loadError && (
        <div className="em-inline-error">
          <AlertCircle size={14} /> {loadError}
          <button className="em-link-btn" onClick={() => loadAll()}>Try again</button>
        </div>
      )}

      <div className="em-stats">
        {[
          { label: 'Total staff',  value: stats?.total ?? 0,    color: 'var(--text)' },
          { label: 'Active',       value: stats?.active ?? 0,   color: 'var(--success)' },
          { label: 'Inactive',     value: stats?.inactive ?? 0, color: 'var(--warning)' },
          { label: 'Designations', value: designations.length,  color: 'var(--accent)' },
          { label: 'Roles',        value: roles.length,         color: 'var(--brand-slate-mist)' },
        ].map(s => (
          <div className="em-stat" key={s.label} style={{ '--em-stat-accent': s.color }}>
            <span className="em-stat-val">{s.value}</span>
            <span className="em-stat-lbl">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="em-tabs-scroll">
        <div className="em-tabs" role="tablist">
          {[
            { id: 'employees',    icon: Users,     label: 'Employees',    count: employees.length },
            { id: 'designations', icon: Briefcase, label: 'Designations', count: designations.length },
            { id: 'roles',        icon: Shield,    label: 'Roles',        count: roles.length },
          ].map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`em-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <t.icon size={16} /> <span className="em-tab-label">{t.label}</span>
              <span className="em-tab-badge">{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── EMPLOYEES ──────────────────────────────────────────────────── */}
      {tab === 'employees' && (
        <>
          <div className="em-toolbar">
            <div className="em-search-wrap">
              <Search size={15} className="em-search-icon" />
              <input
                className="em-search"
                placeholder="Search by name, email, phone or code…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search employees"
              />
              {search && (
                <button className="em-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="em-filter-wrap">
              <Filter size={15} />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="em-select" aria-label="Filter by status">
                <option value="">All status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>

          {!loading && filtered.length > 0 && (search || filterStatus) && (
            <p className="em-result-count">
              Showing {filtered.length} of {employees.length} employees
              <button className="em-link-btn" onClick={() => { setSearch(''); setFilterStatus(''); }}>Clear filters</button>
            </p>
          )}

          {loading ? (
            <div className="em-loading"><div className="em-spinner" /><p>Loading employees…</p></div>
          ) : filtered.length === 0 ? (
            <div className="em-empty">
              <span className="em-empty-icon"><Users size={30} strokeWidth={1.4} /></span>
              <h3>{search || filterStatus ? 'Nothing matches those filters' : 'No employees yet'}</h3>
              <p>{search || filterStatus ? 'Clear the search or status filter to see everyone.' : 'Add your first employee to get started.'}</p>
              {!search && !filterStatus && (
                <button className="em-btn-primary" onClick={() => { setEditEmp(null); setEmpModal(true); }}>
                  <Plus size={16} /> <span>Add employee</span>
                </button>
              )}
            </div>
          ) : isCompact ? (
            <div className="em-emp-cards">
              {filtered.map(emp => (
                <EmployeeCard
                  key={emp._id}
                  emp={emp}
                  actions={empActions(emp)}
                  onView={() => setDetailModal(emp)}
                  onEdit={() => { setEditEmp(emp); setEmpModal(true); }}
                />
              ))}
            </div>
          ) : (
            <div className="em-table-wrap">
              <table className="em-table">
                <thead>
                  <tr>
                    <th className="em-col-emp">Employee</th>
                    <th className="em-col-code">Code</th>
                    <th>Outlets</th>
                    <th>Designations</th>
                    <th className="em-col-status">Status</th>
                    <th className="em-col-actions"><span className="em-sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(emp => (
                    <EmployeeRow
                      key={emp._id}
                      emp={emp}
                      actions={empActions(emp)}
                      onView={() => setDetailModal(emp)}
                      onEdit={() => { setEditEmp(emp); setEmpModal(true); }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── DESIGNATIONS ───────────────────────────────────────────────── */}
      {tab === 'designations' && (
        <>
          <div className="em-section-head">
            <p className="em-section-desc">A designation is the job title you give someone. It bundles the roles below into one thing you can assign.</p>
            <button className="em-btn-primary" onClick={() => { setEditDesig(null); setDesigModal(true); }}>
              <Plus size={16} /> <span>New designation</span>
            </button>
          </div>

          {!loading && designations.length === 0 ? (
            <div className="em-empty">
              <span className="em-empty-icon"><Briefcase size={30} strokeWidth={1.4} /></span>
              <h3>No designations yet</h3>
              <p>Create the titles you actually use — “Head Chef”, “Counter Staff”, “Night Manager”.</p>
              <button className="em-btn-primary" onClick={() => { setEditDesig(null); setDesigModal(true); }}>
                <Plus size={16} /> <span>Create designation</span>
              </button>
            </div>
          ) : (
            <div className="em-card-grid">
              {designations.map(d => (
                <div className="em-desig-card" key={d._id} style={{ '--em-card-accent': d.color }}>
                  <div className="em-desig-card-top">
                    <div className="em-desig-card-ident">
                      <div className="em-desig-card-name">{d.name}</div>
                      {d.description && <p className="em-desig-card-desc">{d.description}</p>}
                    </div>
                    <div className="em-row-actions">
                      <button className="em-icon-btn" title="Edit" aria-label={`Edit ${d.name}`} onClick={() => { setEditDesig(d); setDesigModal(true); }}><Edit2 size={15} /></button>
                      <button className="em-icon-btn em-icon-btn--danger" title="Delete" aria-label={`Delete ${d.name}`} onClick={() => deleteDesignation(d)}><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="em-desig-roles">
                    {(d.roles || []).length === 0
                      ? <span className="em-muted">No roles attached — this grants nothing yet</span>
                      : d.roles.map(r => (
                          <span key={r._id} className="em-chip em-chip--role"><Shield size={11} />{r.name}</span>
                        ))}
                  </div>
                  <div className="em-card-foot">
                    <span><KeyRound size={12} /> {d.permissionCount ?? 0} permissions</span>
                    <span><Users size={12} /> {d.employeeCount ?? 0} staff</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── ROLES ──────────────────────────────────────────────────────── */}
      {tab === 'roles' && (
        <>
          <div className="em-section-head">
            <p className="em-section-desc">Roles are yours to define. Pick any combination of permissions and name it whatever your team calls it.</p>
            <div className="em-btn-group">
              {roles.length === 0 && (
                <button className="em-btn-ghost" onClick={() => run(() => api('/roles/seed', { method: 'POST' }), 'Starter roles added').catch(() => {})}>
                  <Sparkles size={16} /> <span>Add starter roles</span>
                </button>
              )}
              <button className="em-btn-primary" onClick={() => { setEditRole(null); setRoleModal(true); }}>
                <Plus size={16} /> <span>New role</span>
              </button>
            </div>
          </div>

          {!loading && roles.length === 0 ? (
            <div className="em-empty">
              <span className="em-empty-icon"><Shield size={30} strokeWidth={1.4} /></span>
              <h3>No roles yet</h3>
              <p>Start from a suggested set and edit them, or build your own from scratch.</p>
              <div className="em-btn-group">
                <button className="em-btn-ghost" onClick={() => run(() => api('/roles/seed', { method: 'POST' }), 'Starter roles added').catch(() => {})}>
                  <Sparkles size={16} /> <span>Use starter roles</span>
                </button>
                <button className="em-btn-primary" onClick={() => { setEditRole(null); setRoleModal(true); }}>
                  <Plus size={16} /> <span>Create role</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="em-card-grid">
              {roles.map(r => (
                <RoleCard
                  key={r._id}
                  role={r}
                  catalog={catalog}
                  onEdit={() => { setEditRole(r); setRoleModal(true); }}
                  onDuplicate={() => run(() => api(`/roles/${r._id}/duplicate`, { method: 'POST' }), 'Role duplicated').catch(() => {})}
                  onDelete={() => deleteRole(r)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── MODALS ─────────────────────────────────────────────────────── */}
      {empModal && (
        <EmployeeModal
          initial={editEmp}
          properties={properties}
          designations={designations}
          onClose={() => setEmpModal(false)}
          onSave={async (data) => {
            const path   = editEmp ? `/employees/${editEmp._id}` : '/employees';
            const method = editEmp ? 'PUT' : 'POST';
            await run(() => api(path, { method, body: data }), editEmp ? 'Employee updated' : 'Employee added');
            setEmpModal(false);
          }}
        />
      )}

      {desigModal && (
        <DesignationModal
          initial={editDesig}
          roles={roles}
          onClose={() => setDesigModal(false)}
          onSave={async (data) => {
            const path   = editDesig ? `/designations/${editDesig._id}` : '/designations';
            const method = editDesig ? 'PUT' : 'POST';
            await run(() => api(path, { method, body: data }), editDesig ? 'Designation updated' : 'Designation created');
            setDesigModal(false);
          }}
        />
      )}

      {roleModal && (
        <RoleModal
          initial={editRole}
          catalog={catalog}
          onClose={() => setRoleModal(false)}
          onSave={async (data) => {
            const path   = editRole ? `/roles/${editRole._id}` : '/roles';
            const method = editRole ? 'PUT' : 'POST';
            await run(() => api(path, { method, body: data }), editRole ? 'Role updated' : 'Role created');
            setRoleModal(false);
          }}
        />
      )}

      {detailModal && (
        <EmployeeDetail
          employee={detailModal}
          catalog={catalog}
          onClose={() => setDetailModal(null)}
          onEdit={() => { setEditEmp(detailModal); setDetailModal(null); setEmpModal(true); }}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   RoleCard
   ═════════════════════════════════════════════════════════════════════════ */

const RoleCard = ({ role, catalog, onEdit, onDuplicate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const labels = useMemo(() => {
    const byKey = new Map();
    catalog.forEach(m => m.permissions.forEach(p => byKey.set(p.key, { ...p, moduleLabel: m.label })));
    const out = [];
    for (const key of role.permissions || []) {
      if (key === '*') { out.push({ key, label: 'Full access', risk: 'critical' }); continue; }
      if (key.endsWith(':*')) {
        const mod = catalog.find(m => m.key === key.slice(0, -2));
        out.push({ key, label: `All ${mod?.label || key.slice(0, -2)}` });
        continue;
      }
      const p = byKey.get(key);
      out.push(p ? { key, label: p.label, risk: p.risk } : { key, label: key, unknown: true });
    }
    return out;
  }, [role.permissions, catalog]);

  const visible = expanded ? labels : labels.slice(0, 6);

  return (
    <div className="em-role-card" style={{ '--em-card-accent': role.color }}>
      <div className="em-role-card-head">
        <span className="em-role-icon" style={{ background: `${role.color}18`, color: role.color }}>
          <Shield size={16} />
        </span>
        <span className="em-role-name">{role.name}</span>
        {role.isSystemSeeded && <span className="em-chip em-chip--muted">Suggested</span>}
        <div className="em-row-actions em-row-actions--end">
          <button className="em-icon-btn" title="Duplicate" aria-label={`Duplicate ${role.name}`} onClick={onDuplicate}><Copy size={15} /></button>
          <button className="em-icon-btn" title="Edit" aria-label={`Edit ${role.name}`} onClick={onEdit}><Edit2 size={15} /></button>
          <button className="em-icon-btn em-icon-btn--danger" title="Delete" aria-label={`Delete ${role.name}`} onClick={onDelete}><Trash2 size={15} /></button>
        </div>
      </div>

      {role.description && <p className="em-role-desc">{role.description}</p>}

      <div className="em-perm-list">
        {labels.length === 0
          ? <span className="em-muted">No permissions selected — this role grants nothing</span>
          : visible.map(p => (
              <span key={p.key} className={`em-perm-chip ${p.unknown ? 'em-perm-chip--unknown' : ''}`} title={p.key}>
                <Check size={10} />{p.label}
              </span>
            ))}
        {labels.length > 6 && (
          <button className="em-link-btn" onClick={() => setExpanded(v => !v)}>
            {expanded ? 'Show less' : `+${labels.length - 6} more`}
          </button>
        )}
      </div>

      <div className="em-card-foot">
        <span><Briefcase size={12} /> Used by {role.usedByDesignations ?? 0} designation{(role.usedByDesignations ?? 0) === 1 ? '' : 's'}</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   RowActionsMenu — the overflow menu behind the ⋮ on every employee
   ═════════════════════════════════════════════════════════════════════════ */

const RowActionsMenu = ({ items, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const ref = useDismissable(open, () => setOpen(false));

  return (
    <div className="em-menu" ref={ref}>
      <button
        className="em-icon-btn"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        title="More actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className={`em-menu-dropdown em-menu-dropdown--${align}`} role="menu">
          {items.map(it => (
            <button
              key={it.key}
              role="menuitem"
              className={it.divided ? 'em-menu-divided' : undefined}
              onClick={() => { setOpen(false); it.onSelect(); }}
              style={it.color ? { color: it.color } : undefined}
            >
              <it.icon size={14} />{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Employee row (desktop table) & card (phones / narrow tablets)
   ═════════════════════════════════════════════════════════════════════════ */

const StatusBadge = ({ status }) => {
  const color = STATUS_CONFIG[status]?.color || '#888';
  return (
    <span className="em-status-badge" style={{ background: `${color}1f`, color }}>
      <span className="em-status-dot" style={{ background: color }} />
      {STATUS_CONFIG[status]?.label || status}
    </span>
  );
};

const outletChip = a => (
  <span key={a._id || a.cafeId?._id || a.cafeId} className="em-chip em-chip--prop">
    <Building2 size={11} /> {a.cafeId?.name || 'Outlet'}
    {a.isPrimary && <span className="em-dot" title="Primary outlet" />}
  </span>
);

const desigChip = (d, i) => (
  <span
    key={`${d._id}-${i}`}
    className="em-chip"
    style={{ background: `${d.color}1f`, color: d.color, borderColor: `${d.color}40` }}
  >
    {d.name}
  </span>
);

const EmployeeRow = ({ emp, actions, onView, onEdit }) => {
  const c = avatarColor(emp.firstName);
  const allDesigs = (emp.assignments || []).flatMap(a => a.designations || []);
  return (
    <tr>
      <td>
        <div className="em-emp-cell">
          <div className="em-avatar" style={{ background: `${c}22`, color: c }}>
            {initials(emp.firstName, emp.lastName)}
          </div>
          <div className="em-emp-ident">
            <p className="em-emp-name">{emp.firstName} {emp.lastName}</p>
            <p className="em-emp-email" title={emp.email}>{emp.email}</p>
          </div>
        </div>
      </td>
      <td><code className="em-code">{emp.employeeCode || '—'}</code></td>
      <td><ChipRow items={emp.assignments} max={2} render={outletChip} /></td>
      <td><ChipRow items={allDesigs} max={2} render={desigChip} /></td>
      <td><StatusBadge status={emp.status} /></td>
      <td>
        <div className="em-row-actions">
          <button className="em-icon-btn" title="View profile" aria-label="View profile" onClick={onView}><Eye size={16} /></button>
          <button className="em-icon-btn" title="Edit" aria-label="Edit employee" onClick={onEdit}><Edit2 size={16} /></button>
          <RowActionsMenu items={actions} />
        </div>
      </td>
    </tr>
  );
};

const EmployeeCard = ({ emp, actions, onView, onEdit }) => {
  const c = avatarColor(emp.firstName);
  const allDesigs = (emp.assignments || []).flatMap(a => a.designations || []);
  return (
    <article className="em-emp-card">
      <div className="em-emp-card-top">
        <div className="em-avatar" style={{ background: `${c}22`, color: c }}>
          {initials(emp.firstName, emp.lastName)}
        </div>
        <div className="em-emp-ident">
          <p className="em-emp-name">{emp.firstName} {emp.lastName}</p>
          <p className="em-emp-email" title={emp.email}>{emp.email}</p>
        </div>
        <StatusBadge status={emp.status} />
      </div>

      <dl className="em-emp-card-meta">
        <div>
          <dt>Code</dt>
          <dd><code className="em-code">{emp.employeeCode || '—'}</code></dd>
        </div>
        <div>
          <dt>Outlets</dt>
          <dd><ChipRow items={emp.assignments} max={2} render={outletChip} /></dd>
        </div>
        <div>
          <dt>Designations</dt>
          <dd><ChipRow items={allDesigs} max={3} render={desigChip} /></dd>
        </div>
      </dl>

      <div className="em-emp-card-foot">
        <button className="em-btn-ghost em-btn-ghost--sm" onClick={onView}><Eye size={14} /> Profile</button>
        <button className="em-btn-ghost em-btn-ghost--sm" onClick={onEdit}><Edit2 size={14} /> Edit</button>
        <RowActionsMenu items={actions} />
      </div>
    </article>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MODAL: Role
   ═════════════════════════════════════════════════════════════════════════ */

const RoleModal = ({ initial, catalog, onClose, onSave }) => {
  const [name, setName]   = useState(initial?.name || '');
  const [desc, setDesc]   = useState(initial?.description || '');
  const [color, setColor] = useState(initial?.color || PALETTE[0]);
  const [permissions, setPermissions] = useState(initial?.permissions || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!name.trim()) { setError('Give the role a name'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), description: desc.trim(), color, permissions });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell size="em-modal--lg" labelledBy="em-role-modal-title" onClose={onClose}>
      <div className="em-modal-head">
        <h3 id="em-role-modal-title">{initial ? `Edit ${initial.name}` : 'New role'}</h3>
        <button className="em-close-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>

      <div className="em-modal-body">
        {error && <div className="em-inline-error"><AlertCircle size={14} /> {error}</div>}

        <div className="em-form-row">
          <label>Name *
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Night Shift Captain" maxLength={50} />
          </label>
          <label>Colour
            <div className="em-palette">
              {PALETTE.map(c => (
                <button key={c} type="button" className={`em-palette-swatch ${color === c ? 'selected' : ''}`}
                  style={{ background: c }} onClick={() => setColor(c)} aria-label={`Colour ${c}`}>
                  {color === c && <Check size={12} color="#fff" />}
                </button>
              ))}
            </div>
          </label>
        </div>

        <label>Description
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2}
            placeholder="What is this role for? Helps your managers pick the right one." maxLength={300} />
        </label>

        <div className="em-form-block">
          <div className="em-form-section-label">Permissions</div>
          <p className="em-form-note em-muted">Anyone holding a designation with this role gets everything ticked below.</p>
          <PermissionPicker catalog={catalog} value={permissions} onChange={setPermissions} />
        </div>
      </div>

      <div className="em-modal-foot">
        <button className="em-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="em-btn-primary" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Create role'}
        </button>
      </div>
    </ModalShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MODAL: Designation
   ═════════════════════════════════════════════════════════════════════════ */

const DesignationModal = ({ initial, roles, onClose, onSave }) => {
  const [name, setName]   = useState(initial?.name || '');
  const [desc, setDesc]   = useState(initial?.description || '');
  const [color, setColor] = useState(initial?.color || PALETTE[0]);
  const [rank, setRank]   = useState(initial?.rank ?? 100);
  const [selectedRoles, setSelectedRoles] = useState(
    (initial?.roles || []).map(r => r._id || r)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleRole = id =>
    setSelectedRoles(r => (r.includes(id) ? r.filter(x => x !== id) : [...r, id]));

  /** Live preview of what this designation ends up granting. */
  const grantCount = useMemo(() => {
    const set = new Set();
    roles.filter(r => selectedRoles.includes(r._id))
      .forEach(r => (r.permissions || []).forEach(p => set.add(p)));
    return set.size;
  }, [roles, selectedRoles]);

  const submit = async () => {
    if (!name.trim()) { setError('Give the designation a name'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), description: desc.trim(), color, rank: Number(rank) || 100, roles: selectedRoles });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell labelledBy="em-desig-modal-title" onClose={onClose}>
      <div className="em-modal-head">
        <h3 id="em-desig-modal-title">{initial ? `Edit ${initial.name}` : 'New designation'}</h3>
        <button className="em-close-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>

      <div className="em-modal-body">
        {error && <div className="em-inline-error"><AlertCircle size={14} /> {error}</div>}

        <label>Name *
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Head Chef" maxLength={50} />
        </label>
        <label>Description
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} placeholder="Brief description…" maxLength={300} />
        </label>

        <div className="em-form-row">
          <label>Colour tag
            <div className="em-palette">
              {PALETTE.map(c => (
                <button key={c} type="button" className={`em-palette-swatch ${color === c ? 'selected' : ''}`}
                  style={{ background: c }} onClick={() => setColor(c)} aria-label={`Colour ${c}`}>
                  {color === c && <Check size={12} color="#fff" />}
                </button>
              ))}
            </div>
          </label>
          <label>Seniority
            <input type="number" min={1} max={999} value={rank} onChange={e => setRank(e.target.value)} />
            <span className="em-field-hint">Lower sorts higher. 1 = most senior.</span>
          </label>
        </div>

        <div className="em-form-block">
          <div className="em-form-section-label">
            Roles
            <span className="em-perm-count">{grantCount} permissions granted</span>
          </div>
          <div className="em-chip-picker">
            {roles.map(r => {
              const selected = selectedRoles.includes(r._id);
              return (
                <button
                  key={r._id}
                  type="button"
                  className={`em-chip-pick ${selected ? 'selected' : ''}`}
                  style={selected
                    ? { background: r.color, color: '#fff', borderColor: r.color }
                    : { borderColor: r.color, color: r.color }}
                  onClick={() => toggleRole(r._id)}
                  title={`${(r.permissions || []).length} permissions`}
                >
                  {selected && <Check size={12} />} <Shield size={12} /> {r.name}
                </button>
              );
            })}
            {roles.length === 0 && <span className="em-muted">Create a role first, then attach it here.</span>}
          </div>
        </div>
      </div>

      <div className="em-modal-foot">
        <button className="em-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="em-btn-primary" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Create designation'}
        </button>
      </div>
    </ModalShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MODAL: Employee
   ═════════════════════════════════════════════════════════════════════════ */

const EmployeeModal = ({ initial, properties, designations, onClose, onSave }) => {
  const [form, setForm] = useState({
    firstName:   initial?.firstName || '',
    lastName:    initial?.lastName || '',
    email:       initial?.email || '',
    phone:       initial?.phone || '',
    gender:      initial?.gender || '',
    joiningDate: initial?.joiningDate ? String(initial.joiningDate).slice(0, 10) : '',
    employeeCode: initial?.employeeCode || '',
    pin:         '',  // never pre-filled — always blank for security
    salary: {
      amount:    initial?.salary?.amount ?? '',
      currency:  initial?.salary?.currency || 'INR',
      frequency: initial?.salary?.frequency || 'monthly',
    },
    notes: initial?.notes || '',
    assignments: (initial?.assignments || []).map(a => ({
      cafeId:       a.cafeId?._id || a.cafeId || '',
      designations: (a.designations || []).map(d => d._id || d),
      isPrimary:    Boolean(a.isPrimary),
    })),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const usedCafes = new Set(form.assignments.map(a => String(a.cafeId)));
  const freeProperty = properties.find(p => !usedCafes.has(String(p._id)));

  const addAssignment = () => {
    if (!freeProperty) return;
    setForm(f => ({
      ...f,
      assignments: [...f.assignments, {
        cafeId: freeProperty._id,
        designations: [],
        isPrimary: f.assignments.length === 0,
      }],
    }));
  };

  const removeAssignment = i =>
    setForm(f => {
      const next = f.assignments.filter((_, idx) => idx !== i);
      if (next.length && !next.some(a => a.isPrimary)) next[0].isPrimary = true;
      return { ...f, assignments: next };
    });

  const updateAssignment = (i, k, v) =>
    setForm(f => {
      const list = f.assignments.map((a, idx) => (idx === i ? { ...a, [k]: v } : a));
      // Only one primary.
      if (k === 'isPrimary' && v) list.forEach((a, idx) => { a.isPrimary = idx === i; });
      return { ...f, assignments: list };
    });

  const toggleDesig = (i, desigId) =>
    setForm(f => ({
      ...f,
      assignments: f.assignments.map((a, idx) => {
        if (idx !== i) return a;
        const has = a.designations.includes(desigId);
        return { ...a, designations: has ? a.designations.filter(d => d !== desigId) : [...a.designations, desigId] };
      }),
    }));

  const submit = async () => {
    if (!form.firstName.trim()) { setError('First name is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) { setError('Enter a valid email address'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        salary: { ...form.salary, amount: Number(form.salary.amount) || 0 },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell size="em-modal--lg" labelledBy="em-emp-modal-title" onClose={onClose}>
      <div className="em-modal-head">
        <h3 id="em-emp-modal-title">{initial ? 'Edit employee' : 'Add employee'}</h3>
        <button className="em-close-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>

      <div className="em-modal-body">
        {error && <div className="em-inline-error"><AlertCircle size={14} /> {error}</div>}

        <div className="em-form-block">
          <div className="em-form-section-label">Personal details</div>
          <div className="em-form-row">
            <label>First name *
              <input value={form.firstName} onChange={e => setField('firstName', e.target.value)} placeholder="Ramesh" maxLength={50} />
            </label>
            <label>Last name
              <input value={form.lastName} onChange={e => setField('lastName', e.target.value)} placeholder="Kumar" maxLength={50} />
            </label>
          </div>
          <div className="em-form-row">
            <label>Email *
              <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} placeholder="ramesh@example.com" />
            </label>
            <label>Phone
              <input value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="+91 98765 43210" />
            </label>
          </div>
          <div className="em-form-row em-form-row--3">
            <label>Gender
              <select value={form.gender} onChange={e => setField('gender', e.target.value)}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label>Joining date
              <input type="date" value={form.joiningDate} onChange={e => setField('joiningDate', e.target.value)} />
            </label>
            <label>Employee code
              <input value={form.employeeCode} onChange={e => setField('employeeCode', e.target.value)} placeholder="Auto-generated" maxLength={24} />
            </label>
          </div>
        </div>

        <div className="em-form-block">
          <div className="em-form-section-label">ROM Portal Access</div>
          <div className="em-form-row">
            <label>
              {initial ? 'Reset password' : 'Portal password *'}
              <input
                type="password"
                value={form.pin}
                onChange={e => setField('pin', e.target.value)}
                placeholder={initial ? 'Enter new password to reset' : 'Set a login password'}
                autoComplete="new-password"
              />
              <span className="em-field-hint">
                {initial ? 'Leave blank to keep the current one.' : 'They will need this to sign in.'}
              </span>
            </label>
            <p className="em-form-callout">
              <KeyRound size={14} />
              <span>Staff sign in with this password and their employee code at <code>/staff/login?cafe=…</code></span>
            </p>
          </div>
        </div>

        <div className="em-form-block">
          <div className="em-form-section-label">Salary</div>
          <div className="em-form-row em-form-row--3">
            <label>Amount
              <input type="number" min={0} value={form.salary.amount}
                onChange={e => setField('salary', { ...form.salary, amount: e.target.value })} placeholder="0" />
            </label>
            <label>Currency
              <select value={form.salary.currency} onChange={e => setField('salary', { ...form.salary, currency: e.target.value })}>
                {['INR', 'USD', 'EUR', 'GBP', 'AED'].map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label>Paid
              <select value={form.salary.frequency} onChange={e => setField('salary', { ...form.salary, frequency: e.target.value })}>
                {['monthly', 'weekly', 'daily', 'hourly'].map(f => (
                  <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="em-form-block">
          <div className="em-form-section-label">
            Outlets &amp; designations
            <button type="button" className="em-btn-ghost em-btn-ghost--sm" onClick={addAssignment} disabled={!freeProperty}>
              <Plus size={14} /> Assign outlet
            </button>
          </div>

          {form.assignments.length === 0 ? (
            <p className="em-muted em-form-note">
              {properties.length === 0
                ? 'Add a restaurant to your account before assigning staff.'
                : 'Not assigned to any outlet yet. Assign one so their designations take effect.'}
            </p>
          ) : (
            <div className="em-assignment-stack">
              {form.assignments.map((a, i) => (
                <div key={`${a.cafeId}-${i}`} className="em-assignment-block">
                  <div className="em-assignment-header">
                    <select
                      value={a.cafeId}
                      onChange={e => updateAssignment(i, 'cafeId', e.target.value)}
                      aria-label="Outlet"
                    >
                      {properties.map(p => (
                        <option
                          key={p._id}
                          value={p._id}
                          disabled={usedCafes.has(String(p._id)) && String(p._id) !== String(a.cafeId)}
                        >
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <label className="em-checkbox-label">
                      <input type="checkbox" checked={a.isPrimary} onChange={e => updateAssignment(i, 'isPrimary', e.target.checked)} />
                      Primary
                    </label>
                    <button type="button" className="em-icon-btn em-icon-btn--danger" onClick={() => removeAssignment(i)} aria-label="Remove outlet">
                      <X size={15} />
                    </button>
                  </div>
                  <div className="em-desig-picker">
                    <p className="em-picker-label">Designations at this outlet</p>
                    <div className="em-chip-picker">
                      {designations.map(d => {
                        const selected = a.designations.includes(d._id);
                        return (
                          <button
                            key={d._id}
                            type="button"
                            className={`em-chip-pick ${selected ? 'selected' : ''}`}
                            style={selected
                              ? { background: d.color, color: '#fff', borderColor: d.color }
                              : { borderColor: d.color, color: d.color }}
                            onClick={() => toggleDesig(i, d._id)}
                          >
                            {selected && <Check size={12} />} {d.name}
                          </button>
                        );
                      })}
                      {designations.length === 0 && <span className="em-muted">Create designations first.</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <label>Notes
          <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} placeholder="Anything worth recording…" maxLength={2000} />
        </label>
      </div>

      <div className="em-modal-foot">
        <button className="em-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="em-btn-primary" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Add employee'}
        </button>
      </div>
    </ModalShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MODAL: Employee detail
   ═════════════════════════════════════════════════════════════════════════ */

const EmployeeDetail = ({ employee: emp, catalog, onClose, onEdit }) => {
  const [perms, setPerms] = useState(null);
  const [permError, setPermError] = useState('');

  useEffect(() => {
    const ctrl = new AbortController();
    api(`/employees/${emp._id}/permissions`, { signal: ctrl.signal })
      .then(r => setPerms(r.data))
      .catch(err => { if (err.name !== 'AbortError') setPermError(err.message); });
    return () => ctrl.abort();
  }, [emp._id]);

  const labelFor = key => {
    for (const m of catalog) {
      const p = m.permissions.find(x => x.key === key);
      if (p) return `${m.label} · ${p.label}`;
    }
    return key;
  };

  const c = avatarColor(emp.firstName);

  return (
    <ModalShell size="em-modal--detail" labelledBy="em-detail-modal-title" onClose={onClose}>
      <div className="em-modal-head">
        <h3 id="em-detail-modal-title">Employee profile</h3>
        <div className="em-btn-group">
          <button className="em-btn-ghost em-btn-ghost--sm" onClick={onEdit}><Edit2 size={14} /> Edit</button>
          <button className="em-close-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
      </div>

      <div className="em-modal-body">
        <div className="em-detail-hero">
          <div className="em-avatar em-avatar--lg" style={{ background: `${c}22`, color: c }}>
            {initials(emp.firstName, emp.lastName)}
          </div>
          <div className="em-detail-ident">
            <h2 className="em-detail-name">{emp.firstName} {emp.lastName}</h2>
            <div className="em-detail-ident-meta">
              <code className="em-code">{emp.employeeCode || '—'}</code>
              <StatusBadge status={emp.status} />
            </div>
          </div>
        </div>

        <div className="em-detail-grid">
          <div className="em-detail-item"><Mail size={14} /><span>{emp.email}</span></div>
          {emp.phone && <div className="em-detail-item"><Phone size={14} /><span>{emp.phone}</span></div>}
          {emp.joiningDate && (
            <div className="em-detail-item">
              <Calendar size={14} /><span>Joined {new Date(emp.joiningDate).toLocaleDateString('en-IN')}</span>
            </div>
          )}
          {emp.salary?.amount > 0 && (
            <div className="em-detail-item">
              <span className="em-detail-rupee">₹</span>
              <span>{Number(emp.salary.amount).toLocaleString('en-IN')} {emp.salary.currency} / {emp.salary.frequency}</span>
            </div>
          )}
        </div>

        {(emp.assignments || []).length > 0 && (
          <div className="em-form-block">
            <div className="em-form-section-label">Outlets</div>
            <div className="em-assignment-stack">
              {emp.assignments.map((a, i) => (
                <div key={a._id || i} className="em-detail-assignment">
                  <div className="em-detail-cafe">
                    <Building2 size={14} />
                    <strong>{a.cafeId?.name || 'Outlet'}</strong>
                    {a.isPrimary && <span className="em-chip em-chip--primary">Primary</span>}
                  </div>
                  <div className="em-desig-list">
                    {(a.designations || []).length === 0
                      ? <span className="em-muted">No designation here</span>
                      : a.designations.map((d, idx) => desigChip(d, idx))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="em-form-block">
          <div className="em-form-section-label">
            What they can do
            {perms && <span className="em-perm-count">{perms.permissions.length} permissions</span>}
          </div>

          {permError && <p className="em-muted em-form-note">{permError}</p>}
          {!perms && !permError && <p className="em-muted em-form-note">Working it out…</p>}
          {perms && perms.permissions.length === 0 && (
            <p className="em-muted em-form-note">
              Nothing yet. {emp.status !== 'active'
                ? 'Access is suspended while they are not active.'
                : 'Attach a designation with roles to grant access.'}
            </p>
          )}
          {perms && perms.permissions.length > 0 && (
            <div className="em-perm-list">
              {perms.permissions.map(k => (
                <span key={k} className="em-perm-chip" title={(perms.sources?.[k] || []).join(' · ') || k}>
                  <Check size={10} />{labelFor(k)}
                </span>
              ))}
            </div>
          )}
        </div>

        {emp.notes && (
          <div className="em-form-block">
            <div className="em-form-section-label">Notes</div>
            <p className="em-notes-text">{emp.notes}</p>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default EmployeeManagement;